import React, { useState, useEffect } from 'react';
import { ArrowDown, Copy, Check, X } from 'lucide-react';
import './Gallery.css';

// Import images
import img1 from '../assets/images/img1.png';
import img2 from '../assets/images/img2.png';

const Gallery: React.FC = () => {
  const images = [img1, img2];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [copiedLocation, setCopiedLocation] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const handleExploreClick = () => {
    setShowLocationPopup(true);
  };

  const copyLocation = (location: string, coords: string) => {
    navigator.clipboard.writeText(coords);
    setCopiedLocation(location);
    
    // Auto-hide popup after 3 seconds
    setTimeout(() => {
      setShowLocationPopup(false);
      setCopiedLocation('');
    }, 3000);
  };

  return (
    <section id="gallery" className="hero-gallery">
      {/* Background Images with Overlay */}
      <div className="hero-backgrounds">
        {images.map((image, index) => (
          <div
            key={index}
            className={`hero-background ${index === currentImageIndex ? 'active' : ''}`}
            style={{
              backgroundImage: `url(${image})`,
            }}
          />
        ))}
        <div className="hero-overlay" />
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        <div className="container">
          <div className="hero-text">
            <h1 className="hero-title">Welcome to The Town</h1>
            <p className="hero-subtitle">
              Experience the ultimate Minecraft server community where creativity meets collaboration. 
              Build your dreams, forge friendships, and become part of something extraordinary.
            </p>
            <div className="hero-actions">
              <button className="btn hero-btn-primary" onClick={handleExploreClick}>
                Explore Our Area
              </button>
              <button className="btn-secondary hero-btn-secondary" onClick={() => {
                window.open('https://discord.gg/n4WrhyEGKy', '_blank');
              }}>
                Join Our Community
              </button>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="scroll-indicator" onClick={() => {
          const pricingSection = document.getElementById('map');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}>
          <ArrowDown size={24} />
          <span>Discover More</span>
        </div>
      </div>

      {/* Image Indicators */}
      <div className="image-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
            onClick={() => setCurrentImageIndex(index)}
            aria-label={`View image ${index + 1}`}
          />
        ))}
      </div>

      {/* Location Popup */}
      {showLocationPopup && (
        <div className="popup-overlay">
          <div className="popup-content location-popup">
            <button className="popup-close" onClick={() => setShowLocationPopup(false)}>
              <X size={20} />
            </button>
            <div className="popup-header">
              <h3>Explore Our Area</h3>
              <p>Click on a location to copy coordinates to your clipboard</p>
            </div>
            
            <div className="location-options">
              <div className="location-card" onClick={() => copyLocation('Overworld', 'X: -9224, Y: 72, Z: 14976')}>
                <div className="location-icon overworld">🌍</div>
                <div className="location-info">
                  <h4>Overworld Location</h4>
                  <p>X: -9224, Y: 72, Z: 14976</p>
                </div>
                <div className="copy-indicator">
                  <Copy size={16} />
                </div>
              </div>
              
              <div className="location-card" onClick={() => copyLocation('Nether Portal', 'X: -1150, Y: 129, Z: 1875')}>
                <div className="location-icon nether">🔥</div>
                <div className="location-info">
                  <h4>Nether Portal (Roof)</h4>
                  <p>X: -1150, Y: 129, Z: 1875</p>
                </div>
                <div className="copy-indicator">
                  <Copy size={16} />
                </div>
              </div>
            </div>
            
            {copiedLocation && (
              <div className="copy-confirmation">
                <Check size={16} />
                <span>{copiedLocation} coordinates copied!</span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery; 